import React, { useState, useEffect, useReducer, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from './components/ui/Icon';
import PastelButton from './components/ui/PastelButton';
import './FiveStageJournal.css';
import { JOURNAL_PROMPTS, STAGE_CONFIG } from './journalPrompts';

// Get time-of-day based tint (same as ReflectOnToday)
const getTimeOfDayTint = () => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) {
    return 'from-sky-100/20 via-blue-50/10 to-cyan-50/15';
  } else if (hour >= 12 && hour < 17) {
    return 'from-amber-50/15 via-yellow-50/10 to-orange-50/15';
  } else if (hour >= 17 && hour < 21) {
    return 'from-orange-100/20 via-rose-50/15 to-pink-50/20';
  } else {
    return 'from-indigo-100/20 via-purple-50/15 to-violet-50/20';
  }
};

// Sparkle Drift Component (reused from ReflectOnToday)
const SparkleLayer = ({ count = 5 }) => {
  const sparkles = useMemo(() => 
    Array.from({ length: count }, (_, i) => ({
      id: i,
      left: `${15 + Math.random() * 70}%`,
      delay: i * 1.2,
      duration: 7 + Math.random() * 4,
      size: Math.random() > 0.5 ? 'xs' : 'sm',
      color: ['#c084fc', '#f9a8d4', '#93c5fd', '#fcd34d'][i % 4],
    })), [count]
  );

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none motion-reduce:hidden" aria-hidden="true">
      {sparkles.map((sparkle) => (
        <motion.div
          key={sparkle.id}
          className="absolute"
          style={{ left: sparkle.left, bottom: '-20px' }}
          initial={{ y: 0, opacity: 0 }}
          animate={{
            y: [0, -350, -500],
            opacity: [0, 0.6, 0],
            x: [0, Math.sin(sparkle.id) * 25, Math.sin(sparkle.id) * 40],
          }}
          transition={{
            duration: sparkle.duration,
            repeat: Infinity,
            delay: sparkle.delay,
            ease: 'easeOut',
          }}
        >
          <Icon name="sparkle" size={sparkle.size} color={sparkle.color} />
        </motion.div>
      ))}
    </div>
  );
};

// Stage Chip Component
const StageChip = ({ stage, index, isActive, isCompleted, progress, onClick }) => {
  return (
    <motion.button
      onClick={onClick}
      className={`
        relative flex flex-col items-center justify-center
        min-w-[48px] sm:min-w-[56px] max-w-[56px] sm:max-w-[64px] py-1 sm:py-2 px-0.5 sm:px-1
        rounded-xl sm:rounded-2xl border-2 backdrop-blur-sm
        transition-colors duration-200
        ${isActive 
          ? 'bg-white/50 border-white/60 shadow-lg' 
          : isCompleted
            ? 'bg-emerald-50/40 border-emerald-200/50'
            : 'bg-white/25 border-white/30 opacity-80 hover:opacity-100'
        }
      `}
      style={{
        boxShadow: isActive 
          ? `0 8px 20px -4px rgba(0,0,0,0.1), 0 0 15px -3px ${stage.color}40`
          : '0 4px 12px -2px rgba(0,0,0,0.06)',
      }}
      whileHover={{ 
        y: -3, 
        boxShadow: '0 10px 25px -5px rgba(0,0,0,0.12)',
      }}
      whileTap={{ scale: 0.96 }}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
    >
      {/* Active glow ring */}
      {isActive && (
        <motion.div
          className="absolute inset-0 rounded-2xl"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{
            boxShadow: `0 0 0 2px rgba(255,255,255,0.7), 0 0 20px 3px ${stage.color}30`,
          }}
        />
      )}
      
      {/* Completion check badge */}
      {isCompleted && (
        <motion.div 
          className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-400 rounded-full flex items-center justify-center shadow-sm"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', delay: 0.1 }}
        >
          <span className="text-white text-[10px]">✓</span>
        </motion.div>
      )}
      
      {/* Icon */}
      <div className="text-base sm:text-lg mb-0.5" aria-hidden="true">{stage.icon}</div>
      
      {/* Title */}
      <div className="text-[9px] sm:text-[10px] font-semibold text-gray-700 leading-tight text-center px-0 sm:px-0.5 line-clamp-2">
        {stage.title}
      </div>
      
      {/* Progress bar */}
      <div className="w-full h-1 mt-1.5 rounded-full bg-white/40 overflow-hidden">
        <motion.div 
          className="h-full rounded-full"
          style={{ backgroundColor: stage.color }}
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        />
      </div>
    </motion.button>
  );
};

// Progress Bar Component
const ProgressBar = ({ currentStage, totalStages, stageColor }) => {
  const progress = ((currentStage + 1) / totalStages) * 100;
  
  return (
    <div className="w-full max-w-xs mx-auto mb-4">
      <div className="h-2 rounded-full bg-white/30 backdrop-blur-sm overflow-hidden shadow-inner">
        <motion.div
          className="h-full rounded-full relative"
          style={{ 
            background: `linear-gradient(90deg, ${stageColor}90, ${stageColor})`,
          }}
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          {/* Subtle glow on fill edge */}
          <div 
            className="absolute right-0 top-0 bottom-0 w-4"
            style={{
              background: `linear-gradient(90deg, transparent, ${stageColor}60)`,
              boxShadow: `0 0 8px 2px ${stageColor}40`,
            }}
          />
        </motion.div>
      </div>
    </div>
  );
};

// State machine for managing the complex journaling flow
const initialState = {
  currentStage: 0,
  currentPrompt: 0,
  responses: {},
  currentResponse: '',
  isCompleted: false,
  isSaving: false,
  completedStages: new Set()
};

const journalReducer = (state, action) => {
  switch (action.type) {
    case 'SET_STAGE':
      return { 
        ...state, 
        currentStage: action.payload, 
        currentPrompt: 0,
        currentResponse: ''
      };
    case 'SET_PROMPT':
      return { ...state, currentPrompt: action.payload, currentResponse: '' };
    case 'SET_RESPONSE':
      return { ...state, currentResponse: action.payload };
    case 'SAVE_RESPONSE':
      const stageId = action.stageId;
      const promptId = action.promptId;
      const responseKey = `${stageId}_${promptId}`;
      const newCompletedStages = new Set(state.completedStages);
      
      // Check if stage is complete
      const stageConfig = Object.values(STAGE_CONFIG)[state.currentStage];
      const stagePrompts = JOURNAL_PROMPTS[stageConfig.promptKey];
      const stageResponses = Object.keys(state.responses).filter(key => 
        key.startsWith(`${stageId}_`)
      );
      
      if (stageResponses.length + 1 >= stagePrompts.length) {
        newCompletedStages.add(stageId);
      }
      
      return {
        ...state,
        responses: {
          ...state.responses,
          [responseKey]: action.response
        },
        completedStages: newCompletedStages,
        currentResponse: ''
      };
    case 'SET_SAVING':
      return { ...state, isSaving: action.payload };
    case 'LOAD_RESPONSES':
      return { ...state, responses: action.payload };
    case 'COMPLETE_JOURNEY':
      return { ...state, isCompleted: true };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
};

// Convert STAGE_CONFIG to array format for easier iteration
const journalStages = Object.values(STAGE_CONFIG);

// Helper function to replace template variables
const replaceTemplate = (text, petName) => {
  return text.replace(/\{\{petName\}\}/g, petName || 'your pet');
};

const FiveStageJournal = ({ petName, petId, angelPet, onClose, onComplete }) => {
  const [state, dispatch] = useReducer(journalReducer, initialState);
  const [showIntro, setShowIntro] = useState(true);
  
  const timeOfDayTint = useMemo(() => getTimeOfDayTint(), []);
  
  // Get backend URL from environment
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  const currentStage = journalStages[state.currentStage];
  const currentPrompts = currentStage ? JOURNAL_PROMPTS[currentStage.promptKey] : [];
  const currentPromptText = currentPrompts[state.currentPrompt];
  const totalStages = journalStages.length;

  useEffect(() => {
    // Load saved responses from localStorage
    const savedResponses = localStorage.getItem(`petHeaven_fiveStageJournal_${petId}`);
    if (savedResponses) {
      dispatch({ type: 'LOAD_RESPONSES', payload: JSON.parse(savedResponses) });
    }
  }, [petId]);

  const saveToLocalStorage = (responses) => {
    localStorage.setItem(`petHeaven_fiveStageJournal_${petId}`, JSON.stringify(responses));
  };

  const handleSaveResponse = () => {
    if (!state.currentResponse.trim()) return;

    const stageId = currentStage.id;
    const promptId = state.currentPrompt;
    const response = state.currentResponse.trim();
    
    // Replace template variables in the question
    const question = replaceTemplate(currentPromptText, petName);
    
    // OPTIMISTIC UI: Update state immediately for instant feedback
    dispatch({ 
      type: 'SAVE_RESPONSE', 
      stageId, 
      promptId, 
      response 
    });
    
    // Save to localStorage immediately
    const newResponses = {
      ...state.responses,
      [`${stageId}_${promptId}`]: response
    };
    saveToLocalStorage(newResponses);
    
    // Background save with keepalive - continues even if user navigates away
    fetch(`${BACKEND_URL}/api/pets/${petId}/journal?content=${encodeURIComponent(response)}&prompt=${encodeURIComponent(question)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      keepalive: true  // Ensures request completes even if page unloads
    }).catch(error => {
      console.error('Error saving journal entry (background):', error);
    });
  };

  const handleNextPrompt = () => {
    // Save current response if there is one (fire-and-forget)
    if (state.currentResponse.trim()) {
      handleSaveResponse();
    }

    // Move to next prompt or stage IMMEDIATELY (no blocking)
    if (state.currentPrompt < currentPrompts.length - 1) {
      dispatch({ type: 'SET_PROMPT', payload: state.currentPrompt + 1 });
    } else if (state.currentStage < totalStages - 1) {
      dispatch({ type: 'SET_STAGE', payload: state.currentStage + 1 });
    } else {
      // Journey complete
      dispatch({ type: 'COMPLETE_JOURNEY' });
    }
  };

  const handlePreviousPrompt = () => {
    if (state.currentPrompt > 0) {
      dispatch({ type: 'SET_PROMPT', payload: state.currentPrompt - 1 });
    } else if (state.currentStage > 0) {
      const prevStage = journalStages[state.currentStage - 1];
      const prevPrompts = JOURNAL_PROMPTS[prevStage.promptKey];
      dispatch({ type: 'SET_STAGE', payload: state.currentStage - 1 });
      dispatch({ type: 'SET_PROMPT', payload: prevPrompts.length - 1 });
    }
    
    // Load previous response if it exists
    const prevStageId = journalStages[state.currentStage].id;
    const prevPromptId = state.currentPrompt;
    const responseKey = `${prevStageId}_${prevPromptId}`;
    if (state.responses[responseKey]) {
      dispatch({ type: 'SET_RESPONSE', payload: state.responses[responseKey] });
    }
  };

  const handleStageSelect = (stageIndex) => {
    dispatch({ type: 'SET_STAGE', payload: stageIndex });
  };

  const handleSaveAndExit = () => {
    // Save in background (fire-and-forget) and exit immediately
    if (state.currentResponse.trim()) {
      handleSaveResponse();
    }
    onClose();
  };

  const handleComplete = () => {
    // Clear saved data since journey is complete
    localStorage.removeItem(`petHeaven_fiveStageJournal_${petId}`);
    onComplete && onComplete(state.responses);
    onClose();
  };

  const getStageProgress = (stageIndex) => {
    const stage = journalStages[stageIndex];
    const stagePrompts = JOURNAL_PROMPTS[stage.promptKey];
    const stageResponses = Object.keys(state.responses).filter(key => 
      key.startsWith(`${stage.id}_`)
    );
    return (stageResponses.length / stagePrompts.length) * 100;
  };

  const canGoNext = () => {
    return state.currentResponse.trim().length > 0 || 
           Object.keys(state.responses).some(key => 
             key === `${currentStage?.id}_${state.currentPrompt}`
           );
  };

  const canGoPrevious = () => {
    return state.currentStage > 0 || state.currentPrompt > 0;
  };

  const renderIntro = () => (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="relative w-full max-w-md mx-auto bg-white/20 backdrop-blur-md rounded-3xl p-1.5 sm:p-5 shadow-[0_12px_30px_rgba(0,0,0,0.08)] border border-white/40 overflow-hidden"
    >
      {/* Inner highlight */}
      <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none" />
      
      {/* Sparkle Layer - inside glass card like writing screen */}
      <SparkleLayer count={6} />
      
      {/* Radial glow behind avatar area */}
      <div className="absolute top-16 left-1/2 -translate-x-1/2 w-48 h-48 pointer-events-none" aria-hidden="true">
        <div className="absolute inset-0 bg-gradient-radial from-purple-200/30 via-pink-100/15 to-transparent rounded-full blur-2xl" />
      </div>

      {/* Angel Pet Avatar & Welcome Message */}
      <div className="relative text-center mb-5 z-10">
        <motion.div 
          className="relative inline-block mb-3"
          animate={{ scale: [1, 1.03, 1] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        >
          <img 
            src={angelPet || `/angel_images/${petName || 'default'}_angel.png`}
            alt={`Angel ${petName || 'Pet'}`}
            className="w-24 h-24 rounded-full object-cover border-4 border-white/50 shadow-xl"
            onError={(e) => {
              const fallbackImages = [
                '/angel_images/Pookie_20250712_233832.png',
                '/angel_images/Max_20250716_033627.png',
                '/angel_images/Amy_20250708_220423.png',
              ];
              const randomFallback = fallbackImages[Math.floor(Math.random() * fallbackImages.length)];
              e.target.src = randomFallback;
            }}
          />
          {/* Glow ring */}
          <div 
            className="absolute inset-0 rounded-full motion-reduce:hidden"
            style={{
              boxShadow: '0 0 30px 8px rgba(168, 85, 247, 0.2), 0 0 60px 15px rgba(236, 72, 153, 0.1)',
            }}
            aria-hidden="true"
          />
        </motion.div>
        
        {/* Welcome bubble - glassmorphism */}
        <motion.div 
          className="relative bg-white/70 backdrop-blur-md rounded-2xl px-5 py-3 shadow-md border border-white/50 max-w-xs mx-auto"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-pink-100/30 via-purple-100/20 to-blue-100/30 opacity-60" />
          <p className="relative text-sm text-gray-700 font-medium italic leading-relaxed">
            Hi, I'm Angel {petName || 'Max'}. Let's walk through this together.
          </p>
        </motion.div>
      </div>

      {/* Stage Pills - Horizontal scroll */}
      <div className="relative z-10 mb-5 -mx-0.5 sm:mx-0">
        <div className="flex justify-center gap-0.5 sm:gap-2 overflow-x-auto pb-2 px-0 sm:px-1">
          {journalStages.map((stage, index) => (
            <StageChip
              key={stage.id}
              stage={stage}
              index={index}
              isActive={false}
              isCompleted={state.completedStages.has(stage.id)}
              progress={getStageProgress(index)}
              onClick={() => {
                handleStageSelect(index);
                setShowIntro(false);
              }}
            />
          ))}
        </div>
      </div>
      
      {/* Recent Entry Preview */}
      {Object.keys(state.responses).length > 0 && (
        <motion.div 
          className="relative z-10 mb-5"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2 text-center">
            Your Recent Thoughts
          </h3>
          <div className="bg-white/40 backdrop-blur-sm rounded-2xl p-3 border border-white/30">
            {Object.entries(state.responses).slice(-1).map(([key, response]) => {
              const [stageId, promptId] = key.split('_');
              const stage = journalStages.find(s => s.id === stageId);
              const promptIndex = parseInt(promptId);
              const prompts = stage ? JOURNAL_PROMPTS[stage.promptKey] : [];
              const prompt = prompts[promptIndex];
              
              return (
                <div key={key} className="flex items-start gap-2">
                  <div className="flex-shrink-0 text-lg" aria-hidden="true">{stage?.icon}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-700 line-clamp-1">
                      {replaceTemplate(prompt, petName)}
                    </p>
                    <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">
                      {response.substring(0, 80)}...
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Start Writing Button */}
      <div className="relative z-10 flex justify-center">
        <motion.div
          whileHover={{ scale: 1.02, y: -2 }}
          whileTap={{ scale: 0.98 }}
        >
          <PastelButton 
            onClick={() => setShowIntro(false)} 
            className="px-8 text-base font-semibold"
            color="rose"
          >
            <Icon name="sparkle" size="sm" color="primary" animate />
            Start Writing
          </PastelButton>
        </motion.div>
      </div>
    </motion.div>
  );

  const renderStageNavigation = () => (
    <div className="flex justify-center gap-0.5 sm:gap-1.5 overflow-x-auto pt-2 pb-2 px-0 sm:px-2 mb-1 -mx-0.5 sm:mx-0">
      {journalStages.map((stage, index) => (
        <StageChip
          key={stage.id}
          stage={stage}
          index={index}
          isActive={index === state.currentStage}
          isCompleted={state.completedStages.has(stage.id)}
          progress={getStageProgress(index)}
          onClick={() => handleStageSelect(index)}
        />
      ))}
    </div>
  );

  const renderCurrentPrompt = () => {
    if (!currentStage || !currentPromptText) return null;
    
    const questionText = replaceTemplate(currentPromptText, petName);
    const placeholderText = `Share your thoughts about ${petName || 'your pet'}...`;
    
    return (
      <motion.div 
        className="flex flex-col"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Prompt indicator */}
        <p className="text-xs text-gray-500 text-center mb-3 font-medium">
          Prompt {state.currentPrompt + 1} of {currentPrompts.length}
        </p>
        
        {/* Prompt card - glassmorphism */}
        <div className="relative bg-white/50 backdrop-blur-md rounded-2xl p-4 mb-4 border border-white/40 shadow-sm">
          <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-white/20 pointer-events-none" />
          <p className="text-gray-700 text-base leading-relaxed font-medium relative z-10">
            {questionText}
          </p>
        </div>
        
        {/* Textarea - premium style */}
        <textarea
          id="journal-response"
          name="journal-response"
          value={state.currentResponse}
          onChange={(e) => dispatch({ type: 'SET_RESPONSE', payload: e.target.value })}
          placeholder={placeholderText}
          className="min-h-[160px] w-full p-4 
            bg-white/40 backdrop-blur-sm 
            border border-white/40 rounded-2xl 
            text-gray-700 placeholder-gray-400
            focus:outline-none focus:ring-2 focus:ring-purple-300/50 focus:border-purple-200/50
            transition-all duration-200 resize-none"
          rows={5}
        />
      </motion.div>
    );
  };

  const renderCompletionSummary = () => (
    <motion.div 
      className="text-center py-6"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
    >
      <motion.div 
        className="text-5xl mb-4"
        animate={{ y: [0, -8, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
      >
        🌟
      </motion.div>
      <h2 className="text-xl font-bold text-gray-700 mb-2">
        Your Healing Journey is Complete
      </h2>
      <p className="text-gray-600 text-sm leading-relaxed max-w-xs mx-auto mb-4">
        Thank you for taking this brave step. Your love for {petName || 'your pet'} shines through every word.
      </p>
      <div className="bg-white/40 backdrop-blur-sm rounded-2xl p-4 border border-white/30 max-w-sm mx-auto">
        <p className="text-gray-600 text-sm italic">
          💝 These words create a beautiful tribute to the love you shared. Carry this love with you as you continue to heal.
        </p>
      </div>
    </motion.div>
  );

  if (showIntro) {
    return (
      <div className="healing-journey-bg heaven-background relative min-h-screen">
        {/* Grain/noise overlay */}
        <div 
          className="absolute inset-0 pointer-events-none opacity-[0.03] z-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}
          aria-hidden="true"
        />
        {/* Time-of-day tint overlay */}
        <div className={`absolute inset-0 pointer-events-none bg-gradient-to-br ${timeOfDayTint} z-0`} aria-hidden="true" />
        
        <div className="container min-h-[calc(100vh-96px)] sm:p-4 relative z-10 flex flex-col">
          {/* Header */}
          <div className="relative text-center mb-3 pt-10">
            <button
              onClick={onClose}
              className="absolute top-0 left-0 bg-white/80 backdrop-blur-sm text-gray-600 px-3 py-2 rounded-xl font-medium hover:bg-white/90 transition-all duration-300 shadow-md text-sm"
            >
              ← Back
            </button>
          
            <h1 className="text-lg md:text-xl font-bold text-gray-700 tracking-tight flex items-center justify-center gap-2 pb-4">
              <Icon name="sparkle" size="md" color="primary" animate />
              Five Stage Healing Journey
            </h1>
          </div>

          {/* Main content */}
          <div className="flex-1 flex items-center justify-center px-0.5 sm:px-4 pb-8">
            {renderIntro()}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="healing-journey-bg heaven-background relative min-h-screen">
      {/* Grain/noise overlay */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-[0.03] z-0"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
        aria-hidden="true"
      />
      {/* Time-of-day tint overlay */}
      <div className={`absolute inset-0 pointer-events-none bg-gradient-to-br ${timeOfDayTint} z-0`} aria-hidden="true" />
      
      <div className="container min-h-[calc(100vh-96px)] sm:p-4 relative z-10 flex flex-col">
        {/* Header */}
        <div className="relative text-center mb-3 pt-10">
          <button
            onClick={onClose}
            className="absolute top-0 left-0 bg-white/80 backdrop-blur-sm text-gray-600 px-3 py-2 rounded-xl font-medium hover:bg-white/90 transition-all duration-300 shadow-md text-sm"
          >
            ← Back
          </button>
        
          <h1 className="text-lg md:text-xl font-bold text-gray-700 tracking-tight flex items-center justify-center gap-2">
            <span className="text-xl" aria-hidden="true">{currentStage?.icon}</span>
            {currentStage?.title || 'Healing Journey'}
          </h1>
          <p className="text-xs text-gray-500/80 mt-0.5 font-light tracking-wider uppercase">
            Stage {state.currentStage + 1} of {totalStages}
          </p>
        </div>

        {/* Main Glass Card */}
        <div className="flex-1 flex items-center justify-center px-0.5 sm:px-4 pb-8">
          <div className="relative w-full max-w-md mx-auto bg-white/20 backdrop-blur-md rounded-3xl p-1.5 sm:p-5 shadow-[0_12px_30px_rgba(0,0,0,0.08)] border border-white/40 overflow-hidden">
            {/* Inner highlight */}
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none" />
            
            {/* Sparkles (fewer for journal view) */}
            <SparkleLayer count={3} />
            
            {/* Stage Navigation */}
            <div className="relative z-10">
              {renderStageNavigation()}
            </div>
            
            {/* Content Area */}
            <div className="relative z-10">
              {state.isCompleted ? renderCompletionSummary() : renderCurrentPrompt()}
            </div>
            
            {/* Action Buttons */}
            <div className="relative z-10 mt-1">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                {/* Previous button */}
                {canGoPrevious() && !state.isCompleted && (
                  <motion.div whileHover={{ x: -2 }} whileTap={{ scale: 0.96 }}>
                    <button
                      onClick={handlePreviousPrompt}
                      className="px-4 py-2.5 bg-white/40 backdrop-blur-sm text-gray-600 rounded-xl font-medium hover:bg-white/60 transition-all duration-200 text-sm border border-white/30"
                    >
                      ← Previous
                    </button>
                  </motion.div>
                )}
                
                {/* Spacer when no previous */}
                {(!canGoPrevious() || state.isCompleted) && <div />}
                
                {/* Main actions */}
                <div className="flex gap-2">
                  {!state.isCompleted && (
                    <motion.div whileHover={{ y: -2 }} whileTap={{ scale: 0.96 }}>
                      <button
                        onClick={handleSaveAndExit}
                        disabled={state.isSaving}
                        className="px-4 py-2.5 bg-purple-100/60 backdrop-blur-sm text-purple-700 rounded-xl font-medium hover:bg-purple-100/80 transition-all duration-200 text-sm border border-purple-200/40 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                      >
                        {state.isSaving ? 'Saving...' : 'Save & Exit'}
                      </button>
                    </motion.div>
                  )}
                  
                  <motion.div whileHover={{ y: -2, scale: 1.02 }} whileTap={{ scale: 0.96 }}>
                    {state.isCompleted ? (
                      <button
                        onClick={handleComplete}
                        className="px-5 py-2.5 bg-gradient-to-r from-emerald-400 to-green-500 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200 text-sm"
                      >
                        <span className="flex items-center gap-1.5">
                          <Icon name="sparkle" size="xs" color="#fff" />
                          Complete Journey
                        </span>
                      </button>
                    ) : (
                      <PastelButton 
                        onClick={handleNextPrompt} 
                        disabled={!canGoNext() || state.isSaving}
                        color="rose"
                        className="text-sm"
                      >
                        {state.isSaving ? 'Saving...' : 
                         (state.currentPrompt < currentPrompts.length - 1 ? 'Next Prompt →' :
                          state.currentStage < totalStages - 1 ? 'Next Stage →' : 'Complete')}
                      </PastelButton>
                    )}
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FiveStageJournal;
